/**
 * `plantViewerApi` — standalone RTK Query slice for the Plant Viewer
 * read-path.
 *
 * Split off from `ontologyApi` so the Plant Viewer surface (cypher
 * landing queries, per-id detail queries, live-tag scope resolver,
 * strays) owns its own cache + tag space rather than being lumped
 * into the catch-all `'Graph'` tag that drives a hundred other
 * unrelated queries. The two slices share `ontologyBaseQuery` from
 * `ontology-api-base.ts` so auth / branch headers and the upstream
 * URL stay in one place.
 *
 * Graph-changing mutations fan into this slice through the shared
 * cache-coherence bridge, which invalidates the broad `Plant` tag.
 *
 * The cypher strings, row extractors, and `Plant*` types this file
 * consumes live in the browser-safe `@atbash-labs/ontology-plantview`
 * package; this slice is intentionally a thin shell that only knows
 * about RTK Query (query/mutation definitions, providesTags, the
 * `drainCypherRows` pagination loop).
 */

import { alignTagReadResultsToRequest, siteSlugFromId } from '@atbash-labs/ontology-core'
import { createApi } from '@reduxjs/toolkit/query/react'
import type { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import {
  PLANT_ALARMS_CYPHER,
  PLANT_AREAS_CYPHER,
  PLANT_AREA_DETAIL_CYPHER,
  PLANT_COMPUTE_DETAIL_CYPHER,
  PLANT_COMPUTE_INDEX_CYPHER,
  PLANT_STANDALONE_DATABASE_INDEX_CYPHER,
  PLANT_ENTITY_CONNECTIONS_CYPHER,
  PLANT_ENTITY_RESOLVE_CYPHER,
  PLANT_LABEL_COUNTS_CYPHER,
  PLANT_QUERIES_CYPHER,
  PLANT_QUERY_DETAIL_CYPHER,
  PLANT_PROTECTIVE_FUNCTIONS_CYPHER,
  PLANT_PROTECTIVE_FUNCTION_DETAIL_CYPHER,
  PLANT_EQUIPMENT_PROTECTIVE_FUNCTIONS_CYPHER,
  PLANT_FUNCTION_LOGIC_DIAGRAMS_CYPHER,
  PLANT_LOGIC_SOLVER_DETAIL_CYPHER,
  PLANT_FUNCTION_LOGIC_DIAGRAM_DETAIL_CYPHER,
  PLANT_SAFETY_POINT_DETAIL_CYPHER,
  PLANT_EQUIPMENT_SAFETY_POINTS_CYPHER,
  PLANT_STRAY_COUNTS_CYPHER,
  PLANT_EQUIPMENT_DETAIL_CYPHER,
  PLANT_EQUIPMENT_RELATIONSHIPS_CYPHER,
  PLANT_PART_DETAIL_CYPHER,
  PLANT_VENDOR_DETAIL_CYPHER,
  PLANT_PROCEDURE_DETAIL_CYPHER,
  PLANT_UTILITY_DETAIL_CYPHER,
  PLANT_LINE_OPERATIONS_CYPHER,
  PLANT_LINE_CONTROL_COVERAGE_CYPHER,
  PLANT_LINE_DETAIL_CYPHER,
  PLANT_LINE_INVENTORY_CYPHER,
  PLANT_DCS_NODE_DETAIL_CYPHER,
  PLANT_DCS_TREE_CYPHER,
  PLANT_NETWORK_NODE_DETAIL_CYPHER,
  PLANT_NETWORK_TREE_CYPHER,
  PLANT_PLC_CONTROLLER_TAGS_CYPHER,
  PLANT_PLC_NODE_DETAIL_CYPHER,
  PLANT_PLC_TREE_CYPHER,
  PLANT_PROCESS_DETAIL_CYPHER,
  PLANT_PROCESS_TREE_CYPHER,
  PLANT_PROVIDER_FOLDER_NODE_CYPHER,
  PLANT_PROVIDER_TAGS_CYPHER,
  PLANT_ROBOTICS_TREE_CYPHER,
  PLANT_ROBOT_NODE_DETAIL_CYPHER,
  PLANT_SCHEDULE_GATEWAYS_CYPHER,
  PLANT_SITES_CYPHER,
  PLANT_SITE_OPTIONS_CYPHER,
  PLANT_SITE_DETAIL_CYPHER,
  PLANT_UDT_FOLDER_NODE_CYPHER,
  buildPlantDbQueryBody,
  buildPlantDbQueryUrl,
  buildPlantDbSchemaUrl,
  cypherForTagScope,
  groupAlarmsByDefinition,
  groupAreaSummaries,
  compareByNameThenId,
  mapPlantAlarmRow,
  mapPlantAreaDetail,
  mapPlantAreaSummaryRow,
  mapPlantComputeDetail,
  mapPlantComputeIndexRow,
  mapPlantDbExecuteResult,
  mapPlantDbSchema,
  mapPlantEntity,
  mapPlantEntityConnections,
  mapPlantLabelCounts,
  mapPlantLabelEntitiesPage,
  mapPlantEquipmentDetail,
  mapPlantEquipmentRelationships,
  mapPlantPartDetail,
  mapPlantVendorDetail,
  mapPlantProcedureDetail,
  mapPlantUtilityDetail,
  mapPlantLineControlCoverage,
  mapPlantLineDetail,
  mapPlantLineInventory,
  mapPlantLineOperations,
  mapPlantDcsNodeDetail,
  mapPlantDcsTree,
  mapPlantNetworkNodeDetail,
  mapPlantNetworkTree,
  mapPlantPlcControllerTagsPage,
  mapPlantPlcNodeDetail,
  mapPlantPlcTree,
  mapPlantProcessDetail,
  mapPlantProcessTreeRow,
  mapPlantQueryDetail,
  mapPlantQuerySummaryRow,
  mapPlantProtectiveFunctionDetail,
  mapPlantProtectiveFunctionSummaryRow,
  mapPlantEquipmentProtectiveFunctions,
  mapPlantFunctionLogicDiagramSummaryRow,
  mapPlantLogicSolverDetail,
  mapPlantFunctionLogicDiagramDetail,
  mapPlantSafetyPointDetail,
  mapPlantEquipmentSafetyPoints,
  mapPlantProviderFolderNodeResponse,
  mapPlantProviderTagsPage,
  mapPlantRoboticsTree,
  mapPlantRobotNodeDetail,
  mapPlantScheduleGatewayRow,
  mapPlantSiteDetail,
  mapPlantSiteSummaryRow,
  mapPlantTagsBundle,
  mapPlantUdtFolderNodeResponse,
  normalizeActiveAlarm,
  normalizeLiveTagValues,
  normalizeTagIdReadResults,
  plantLabelEntitiesCypher
} from '@atbash-labs/ontology-plantview'
import type {
  ActiveSchedulesArgs,
  ActiveSchedulesResponse,
  PlantActiveAlarm,
  PlantActiveAlarmsArgs,
  PlantActiveAlarmsGateway,
  PlantActiveAlarmsResponse,
  PlantAlarm,
  PlantAlarmsArgs,
  PlantAlarmsIndex,
  PlantAreaDetail,
  PlantAreaIndex,
  PlantAreaSummary,
  PlantComputeHostDetail,
  PlantComputeHostSummary,
  PlantDbExecuteArgs,
  PlantDbExecuteResult,
  PlantDbSchemaArgs,
  PlantDbSchemaResponse,
  PlantEquipmentDetail,
  PlantEquipmentRelationships,
  PlantPartDetail,
  PlantVendorDetail,
  PlantProcedureDetail,
  PlantUtilityDetail,
  PlantLineOperations,
  PlantEntityConnectionGroup,
  PlantEntityConnectionsArgs,
  PlantEntityResult,
  PlantLabelCount,
  PlantLabelEntitiesArgs,
  PlantLabelEntitiesPage,
  PlantLineControlCoverage,
  PlantLineDetail,
  PlantLineInventoryItem,
  PlantLiveTagValuesArgs,
  PlantTagIdValue,
  PlantTagIdValuesArgs,
  PlantTagIdValuesResponse,
  PlantDcsNodeDetail,
  PlantDcsTree,
  PlantNetworkNodeDetail,
  PlantNetworkTree,
  PlantPlcControllerTagsArgs,
  PlantPlcControllerTagsPage,
  PlantPlcNodeDetail,
  PlantPlcTree,
  PlantProcessDetail,
  PlantProcessTree,
  PlantProcessTreeNode,
  PlantProviderFolderNodeArgs,
  PlantProviderFolderNodeResponse,
  PlantProviderTagsArgs,
  PlantProviderTagsPage,
  PlantQueryDetail,
  PlantQuerySummary,
  PlantProtectiveFunctionDetail,
  PlantProtectiveFunctionSummary,
  PlantEquipmentProtectiveFunctionLink,
  PlantFunctionLogicDiagramSummary,
  PlantLogicSolverDetail,
  PlantFunctionLogicDiagramDetail,
  PlantSafetyPointDetail,
  PlantEquipmentSafetyPointLink,
  PlantRoboticsTree,
  PlantRobotNodeDetail,
  PlantScheduleGateway,
  PlantSiteDetail,
  PlantSiteSummary,
  PlantTagScope,
  PlantTagValuesResponse,
  PlantTagsBundle,
  PlantUdtFolderNodeArgs,
  PlantUdtFolderNodeResponse
} from '@atbash-labs/ontology-plantview'

import {
  COMPONENT_BY_NAME_CYPHER,
  EQUIPMENT_BY_NAME_CYPHER,
  EQUIPMENT_BY_TAG_CYPHER,
  TAG_ANCHOR_CYPHER,
  componentScopeForLabel,
  pickComponentByName,
  tagIdCandidate
} from '@atbash-labs/ontology-graph'
import type {
  ComponentByNameRow,
  EquipmentByNameRow,
  EquipmentByTagRow,
  TagAnchorRef,
  TagAnchorRow
} from '@atbash-labs/ontology-graph'

import { getOntologyServiceBaseUrl } from '../../config'
import {
  CypherDrainBaseQuery,
  CypherReadResult,
  ONTOLOGY_BRANCH_HEADER,
  drainCypherRows,
  ontologyBaseQuery
} from '../core'
import { plantComponentRefKey, type PMComponentRef } from '../shared'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

/** Minimal Site picker row (id + display name). */
export interface PlantSiteOption {
  id: string
  name: string
}

/** Match the proxy's per-request ceiling without dropping large Plant scopes. */
const LIVE_TAG_READ_BATCH_SIZE = 500
/** Bound polling fan-out for scopes backed by thousands of tags. */
const LIVE_TAG_READ_CONCURRENCY = 3
/** Error code on the row added for a node ID that a successful `/tags/read` response left out. */
export const TAG_RESULT_OMITTED_ERROR_CODE = 'TAG_RESULT_OMITTED'

function mapSiteOptionRow(row: Record<string, unknown>): PlantSiteOption | null {
  const id = typeof row['id'] === 'string' ? row['id'].trim() : ''
  if (!id) return null
  const rawName = typeof row['name'] === 'string' && row['name'].trim() ? row['name'].trim() : id
  const name = rawName.startsWith('urn:') ? siteSlugFromId(id) || rawName : rawName
  return { id, name }
}

/**
 * Render a `FetchBaseQueryError` as the most user-meaningful string we
 * can, prioritizing structured `{error}` / `{message}` payloads over
 * the raw HTTP status. Used by the `getPlantActiveAlarms` per-gateway
 * fan-out so the page can surface "gateway X failed: timeout" without
 * losing the rest of the batch.
 */
function extractFetchErrorMessage(err: FetchBaseQueryError): string {
  const data = (err as { data?: unknown }).data
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>
    if (typeof d['error'] === 'string') return d['error'] as string
    if (typeof d['message'] === 'string') return d['message'] as string
  }
  if ('status' in err) {
    if (typeof err.status === 'number') return `HTTP ${err.status}`
    if (typeof err.status === 'string') return err.status
  }
  return 'Unknown error'
}

async function readPlantLiveTagValuesById(
  baseQuery: CypherDrainBaseQuery,
  requestedNodeIds: string[]
): Promise<PlantTagIdValuesResponse> {
  const nodeIds = [...new Set(requestedNodeIds.map((id) => id.trim()).filter(Boolean))]
  if (nodeIds.length === 0) return { tags: [] }

  const batches: string[][] = []
  for (let i = 0; i < nodeIds.length; i += LIVE_TAG_READ_BATCH_SIZE) {
    batches.push(nodeIds.slice(i, i + LIVE_TAG_READ_BATCH_SIZE))
  }

  const results: PlantTagIdValue[][] = Array.from({ length: batches.length }, () => [])
  let nextBatch = 0
  const worker = async () => {
    for (;;) {
      const batchIndex = nextBatch++
      const batch = batches[batchIndex]
      if (!batch) return

      const response = await baseQuery({
        url: '/tags/read',
        method: 'POST',
        body: { node_ids: batch }
      })
      if (response.error) {
        const message = extractFetchErrorMessage(response.error)
        results[batchIndex] = batch.map((nodeId) => ({
          nodeId,
          path: '',
          value: undefined,
          transportError: true,
          error: message
        }))
        continue
      }

      const raw = response.data as { results?: unknown } | undefined
      results[batchIndex] = alignTagReadResultsToRequest(
        batch,
        normalizeTagIdReadResults(raw?.results),
        (nodeId) => ({
          nodeId,
          path: '',
          value: undefined,
          isGood: false,
          transportError: false,
          errorCode: TAG_RESULT_OMITTED_ERROR_CODE,
          error: 'Tag read response omitted the requested node ID.'
        })
      )
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(LIVE_TAG_READ_CONCURRENCY, batches.length) }, () => worker())
  )
  return { tags: results.flat() }
}

export const PLANT_VIEWER_TAG_TYPES = ['Plant'] as const
export type PlantViewerTagType = (typeof PLANT_VIEWER_TAG_TYPES)[number]

/**
 * One equipment group from a config's tag references, keyed by the display
 * segment parsed out of its paths (e.g. `CAP-401`). Callers should sort
 * groups and paths so the RTK cache key is stable across renders.
 */
export interface PlantEquipmentRefsGroup {
  name: string
  /** Raw tag references in the group — Ignition paths or tag URNs. */
  paths: string[]
}

/** Resolved `:Equipment` refs keyed by group name; unresolved groups are absent. */
export type PlantEquipmentRefs = Record<string, PMComponentRef>

/**
 * Resolved insight-chip refs keyed by `{scope}:{rawId}`. Unresolved inputs
 * are absent so the caller can mark those chips unlinkable.
 */
export type PlantComponentRefs = Record<string, PMComponentRef>

/**
 * These UNWIND helpers emit at most one row per input, so chunking bounds
 * both the request body and the response for a dashboard-sized batch.
 */
const CYPHER_UNWIND_CHUNK = 100

function chunkArray<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

function isResolvedEquipmentId(id: string): boolean {
  return id.startsWith('urn:equipment:')
}

function isResolvedLineOrProcessId(id: string): boolean {
  return (
    id.startsWith('urn:line:') ||
    id.startsWith('urn:process:') ||
    id.includes('/line:') ||
    id.includes('/process:')
  )
}

export const plantViewerApi = createApi({
  reducerPath: 'plantViewerApi',
  baseQuery: ontologyBaseQuery,
  refetchOnReconnect: true,
  tagTypes: PLANT_VIEWER_TAG_TYPES,
  endpoints: (builder) => ({
    getPlantEntity: builder.query<PlantEntityResult | null, string>({
      query: (id) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_ENTITY_RESOLVE_CYPHER, params: { id } }
      }),
      transformResponse: (raw: CypherReadResult): PlantEntityResult | null => mapPlantEntity(raw),
      providesTags: (_result, _error, id) => [{ type: 'Plant', id: `Entity:${id}` }, 'Plant']
    }),
    getPlantEntityConnections: builder.query<
      PlantEntityConnectionGroup[],
      PlantEntityConnectionsArgs
    >({
      query: ({ id }) => ({
        url: '/cypher/read',
        method: 'POST',
        body: {
          query: PLANT_ENTITY_CONNECTIONS_CYPHER,
          params: { id }
        }
      }),
      transformResponse: (raw: CypherReadResult, _meta, { id }): PlantEntityConnectionGroup[] =>
        mapPlantEntityConnections(raw, id),
      providesTags: (_result, _error, { id }) => [
        { type: 'Plant', id: `EntityConnections:${id}` },
        'Plant'
      ]
    }),
    getPlantLabelCounts: builder.query<PlantLabelCount[], void>({
      query: () => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_LABEL_COUNTS_CYPHER, params: {} }
      }),
      transformResponse: (raw: CypherReadResult): PlantLabelCount[] => mapPlantLabelCounts(raw),
      providesTags: ['Plant']
    }),
    getPlantStrayCounts: builder.query<PlantLabelCount[], void>({
      query: () => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_STRAY_COUNTS_CYPHER, params: {} }
      }),
      transformResponse: (raw: CypherReadResult): PlantLabelCount[] => mapPlantLabelCounts(raw),
      providesTags: ['Plant']
    }),
    getPlantLabelEntities: builder.query<PlantLabelEntitiesPage, PlantLabelEntitiesArgs>({
      query: ({
        label,
        search,
        validity = 'all',
        stray = 'all',
        minConfidence,
        page,
        pageSize = 50
      }) => {
        const resolvedPage = Math.max(0, Math.floor(page))
        const resolvedPageSize = Math.max(1, Math.floor(pageSize))
        return {
          url: '/cypher/read',
          method: 'POST',
          body: {
            query: plantLabelEntitiesCypher(label),
            params: {
              search: search?.trim() || null,
              validity,
              stray,
              minConfidence:
                minConfidence === undefined ? null : Math.min(1, Math.max(0, minConfidence)),
              skip: resolvedPage * resolvedPageSize,
              pageSize: resolvedPageSize
            }
          }
        }
      },
      transformResponse: (raw: CypherReadResult, _meta, { label }): PlantLabelEntitiesPage =>
        mapPlantLabelEntitiesPage(raw, label),
      providesTags: (_result, _error, { label }) => [
        { type: 'Plant', id: `Label:${label}` },
        'Plant'
      ]
    }),
    /**
     * All sites with the per-site counts the Equipment-tab landing
     * grid needs in one round-trip. `equipmentCount` walks every
     * site's top-level lines and any nested sublines
     * (`HAS_SUBLINE*0..`) to count the distinct `Equipment` reachable
     * from that site.
     *
     * Drained client-side so the grid gets every site rather than the
     * proxy's first page — see `drainCypherRows` for the full rationale.
     */
    getPlantSites: builder.query<PlantSiteSummary[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(baseQuery as CypherDrainBaseQuery, PLANT_SITES_CYPHER)
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const out: PlantSiteSummary[] = []
        for (const row of drained.data?.results ?? []) {
          const summary = mapPlantSiteSummaryRow(row)
          if (summary) out.push(summary)
        }
        return { data: out, meta: drained.meta }
      },
      providesTags: ['Plant']
    }),

    /**
     * Compact top-level Line names for chat suggested prompts. Not a
     * Plant Viewer landing query — bounded to a handful of rows so
     * opening the rail does not drain every site.
     */
    getPlantLineInventory: builder.query<PlantLineInventoryItem[], void>({
      query: () => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_LINE_INVENTORY_CYPHER, params: {} }
      }),
      transformResponse: (raw: CypherReadResult): PlantLineInventoryItem[] =>
        mapPlantLineInventory(raw),
      providesTags: ['Plant']
    }),

    /**
     * Site id/name options for a single ontology branch — `branchId` when
     * given, otherwise whatever `X-Ontology-Branch` the base query sends.
     * Ingest pickers use this with the active branch so a site is only
     * offered where its `:Site` node actually exists; sites that live on
     * other branches, on deleted branches, or only as leftover objects in
     * storage are not merged in. Keying the cache by branch id means a
     * branch switch can never serve the previous branch's rows.
     */
    getPlantSiteOptions: builder.query<PlantSiteOption[], { branchId?: string } | void>({
      queryFn: async (arg, _api, _extra, baseQuery) => {
        const branchId = arg?.branchId?.trim()
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_SITE_OPTIONS_CYPHER,
          {},
          undefined,
          branchId ? { headers: { [ONTOLOGY_BRANCH_HEADER]: branchId } } : {}
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }

        const byId = new Map<string, PlantSiteOption>()
        for (const row of drained.data?.results ?? []) {
          const opt = mapSiteOptionRow(row)
          if (!opt) continue
          const existing = byId.get(opt.id)
          if (!existing || (existing.name === existing.id && opt.name !== opt.id)) {
            byId.set(opt.id, opt)
          }
        }

        return {
          data: [...byId.values()].sort((a, b) => a.name.localeCompare(b.name)),
          meta: drained.meta
        }
      },
      providesTags: ['Plant']
    }),

    /**
     * Detail bundle for a single Site: every top-level line
     * (`Site -[:HAS_LINE]-> Line`), each with the Process it
     * instantiates, its direct subline count, and its equipment count
     * (including sublines), plus the directed `Line -[:FEEDS]-> Line`
     * edges between the lines on this site for the connectivity
     * diagram.
     */
    getPlantSiteDetail: builder.query<PlantSiteDetail, string>({
      query: (siteId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_SITE_DETAIL_CYPHER, params: { siteId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, siteId): PlantSiteDetail =>
        mapPlantSiteDetail(raw, siteId),
      providesTags: (_r, _e, siteId) => [{ type: 'Plant', id: `Site:${siteId}` }, 'Plant']
    }),

    /**
     * Detail bundle for a single Line OR Subline (same shape —
     * sublines are also `:Line` nodes attached via `HAS_SUBLINE`).
     */
    getPlantLineDetail: builder.query<PlantLineDetail, string>({
      query: (lineId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_LINE_DETAIL_CYPHER, params: { lineId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, lineId): PlantLineDetail =>
        mapPlantLineDetail(raw, lineId),
      providesTags: (_r, _e, lineId) => [{ type: 'Plant', id: `Line:${lineId}` }, 'Plant']
    }),

    /**
     * PLC control-coverage roll-up for a line and its full
     * `HAS_SUBLINE` subtree. The flat `lines` list is stitched into a
     * nested tree client-side so `LineDetailPage` can render a
     * "Control coverage" section.
     */
    getPlantLineControlCoverage: builder.query<PlantLineControlCoverage, string>({
      query: (lineId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_LINE_CONTROL_COVERAGE_CYPHER, params: { lineId } }
      }),
      transformResponse: (raw: CypherReadResult): PlantLineControlCoverage =>
        mapPlantLineControlCoverage(raw),
      providesTags: (_r, _e, lineId) => [{ type: 'Plant', id: `LineControl:${lineId}` }, 'Plant']
    }),

    /**
     * Detail bundle for a single Process: parent (via
     * `<-[:HAS_SUBPROCESS]-`), direct children, and every Line/Subline
     * that `INSTANTIATES` the process across all sites.
     */
    getPlantProcessDetail: builder.query<PlantProcessDetail, string>({
      query: (processId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_PROCESS_DETAIL_CYPHER, params: { processId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, processId): PlantProcessDetail =>
        mapPlantProcessDetail(raw, processId),
      providesTags: (_r, _e, processId) => [{ type: 'Plant', id: `Process:${processId}` }, 'Plant']
    }),

    /**
     * Global Process tree, returned flat with `parentId` set from the
     * `(:Process)-[:HAS_SUBPROCESS]->(:Process)` edge. The client
     * builds the actual tree by grouping on `parentId`.
     */
    getPlantProcessTree: builder.query<PlantProcessTree, void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_PROCESS_TREE_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const nodes: PlantProcessTreeNode[] = []
        for (const row of drained.data?.results ?? []) {
          const node = mapPlantProcessTreeRow(row)
          if (node) nodes.push(node)
        }
        return { data: { nodes }, meta: drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'ProcessTree' }, 'Plant']
    }),

    /**
     * Detail bundle for a single Equipment: the equipment node, every
     * sensor target wired via the spec-mandated indirected chain
     * `Equipment -[:HAS_OBSERVABLE|HAS_SETPOINT|HAS_PROPERTY]-> {…}
     * -[:MEASURED_BY|SET_BY]-> {Tag | PLCTag | UDTInstance}`, every
     * `UDTInstance` this equipment directly `MATERIALIZES`, plus the
     * parent line + site + process and the upstream `Compute`.
     */
    getPlantEquipmentDetail: builder.query<PlantEquipmentDetail, string>({
      query: (equipmentId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_EQUIPMENT_DETAIL_CYPHER, params: { equipmentId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, equipmentId): PlantEquipmentDetail =>
        mapPlantEquipmentDetail(raw, equipmentId),
      providesTags: (_r, _e, equipmentId) => [
        { type: 'Plant', id: `Equipment:${equipmentId}` },
        'Plant'
      ]
    }),

    /**
     * Below-the-fold relationship panels for a single Equipment: BOM
     * (`HAS_PART` depth ≤ 8), physical connections, procedures, fault
     * knowledge, area placement, and inbound MES usage. Kept separate
     * from `getPlantEquipmentDetail` for failure isolation.
     */
    getPlantEquipmentRelationships: builder.query<PlantEquipmentRelationships, string>({
      query: (equipmentId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: {
          query: PLANT_EQUIPMENT_RELATIONSHIPS_CYPHER,
          params: { equipmentId }
        }
      }),
      transformResponse: (raw: CypherReadResult, _meta, equipmentId): PlantEquipmentRelationships =>
        mapPlantEquipmentRelationships(raw, equipmentId),
      providesTags: (_r, _e, equipmentId) => [
        { type: 'Plant', id: `Equipment:${equipmentId}` },
        'Plant'
      ]
    }),

    /** Detail bundle for a single Part and its maintenance relationships. */
    getPlantPartDetail: builder.query<PlantPartDetail, string>({
      query: (partId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_PART_DETAIL_CYPHER, params: { partId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, partId): PlantPartDetail =>
        mapPlantPartDetail(raw, partId),
      providesTags: (_r, _e, partId) => [{ type: 'Plant', id: `Part:${partId}` }, 'Plant']
    }),

    /** Detail bundle for a Vendor, its Parts, and reachable Equipment. */
    getPlantVendorDetail: builder.query<PlantVendorDetail, string>({
      query: (vendorId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_VENDOR_DETAIL_CYPHER, params: { vendorId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, vendorId): PlantVendorDetail =>
        mapPlantVendorDetail(raw, vendorId),
      providesTags: (_r, _e, vendorId) => [{ type: 'Plant', id: `Vendor:${vendorId}` }, 'Plant']
    }),

    /**
     * Detail bundle for a single Procedure: full node, PROCEDURE_FOR
     * peers (with labels), REQUIRES_PART / REQUIRES_SKILL_LEVEL rows,
     * and APPLIES_TO_PRODUCT chips.
     */
    getPlantProcedureDetail: builder.query<PlantProcedureDetail, string>({
      query: (procedureId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_PROCEDURE_DETAIL_CYPHER, params: { procedureId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, procedureId): PlantProcedureDetail =>
        mapPlantProcedureDetail(raw, procedureId),
      providesTags: (_r, _e, procedureId) => [
        { type: 'Plant', id: `Procedure:${procedureId}` },
        'Plant'
      ]
    }),

    /**
     * Detail bundle for a single Utility: full node + inbound consumers
     * via `REQUIRES_UTILITY` (equipment + edge connection specs).
     */
    getPlantUtilityDetail: builder.query<PlantUtilityDetail, string>({
      query: (utilityId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_UTILITY_DETAIL_CYPHER, params: { utilityId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, utilityId): PlantUtilityDetail =>
        mapPlantUtilityDetail(raw, utilityId),
      providesTags: (_r, _e, utilityId) => [{ type: 'Plant', id: `Utility:${utilityId}` }, 'Plant']
    }),

    /**
     * LineOperation (preferred) or legacy HumanLabor bundle for a Line.
     * Equipment inventory still comes from line detail. Kept separate
     * from `getPlantLineDetail`.
     */
    getPlantLineOperations: builder.query<PlantLineOperations, string>({
      query: (lineId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_LINE_OPERATIONS_CYPHER, params: { lineId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, lineId): PlantLineOperations =>
        mapPlantLineOperations(raw, lineId),
      providesTags: (_r, _e, lineId) => [{ type: 'Plant', id: `Line:${lineId}` }, 'Plant']
    }),

    /**
     * Resolve a config's tag references to the plant components that own
     * them so config equipment links land on the same page the run/insight
     * chips do. Three-step ladder, first hit per group wins:
     *
     *   1. Anchor each reference to a `:Tag` node — full URNs are exact
     *      ids; paths match persisted `Tag.tag_path`.
     *   2. Walk anchored tags with the SAME cypher the agent runners use
     *      to resolve component refs (`EQUIPMENT_BY_TAG_CYPHER`) — owning
     *      equipment preferred, owning line as the fallback (the run
     *      resolver's equipment > line precedence).
     *   3. Groups still unresolved fall back to exact-name equality on
     *      their parsed display segment (`EQUIPMENT_BY_NAME_CYPHER`).
     *
     * Each step returns at most one row per input, so realistic config
     * sizes sit well inside a single page; an oversized config degrades
     * to unresolved groups (plain badges), never a broken link.
     */
    getPlantEquipmentRefs: builder.query<PlantEquipmentRefs, PlantEquipmentRefsGroup[]>({
      queryFn: async (groups, _api, _extra, baseQuery) => {
        const read = async (query: string, params: Record<string, unknown>) => {
          const res = await (baseQuery as CypherDrainBaseQuery)({
            url: '/cypher/read',
            method: 'POST',
            body: { query, params }
          })
          if (res.error) return { error: res.error }
          return {
            rows: (res.data as CypherReadResult | undefined)?.results ?? []
          }
        }

        const candidatesByGroup = groups.map((group) => ({
          name: group.name,
          candidates: group.paths.map(tagIdCandidate)
        }))
        const anchorRefs = new Map<string, TagAnchorRef>()
        for (const { candidates } of candidatesByGroup) {
          for (const c of candidates) {
            if (c.kind === 'anchor') anchorRefs.set(c.ref.path, c.ref)
          }
        }

        const tagIdByPath = new Map<string, string>()
        if (anchorRefs.size > 0) {
          const refs = [...anchorRefs.values()].sort((a, b) => a.path.localeCompare(b.path))
          const res = await read(TAG_ANCHOR_CYPHER, { refs })
          if (res.error) return { error: res.error }
          for (const row of res.rows as unknown as TagAnchorRow[]) {
            if (row.tag_id) tagIdByPath.set(row.path, row.tag_id)
          }
        }

        const groupsByTagId = new Map<string, string[]>()
        for (const { name, candidates } of candidatesByGroup) {
          for (const candidate of candidates) {
            const id = candidate.kind === 'id' ? candidate.id : tagIdByPath.get(candidate.ref.path)
            if (!id) continue
            const names = groupsByTagId.get(id) ?? []
            if (!names.includes(name)) names.push(name)
            groupsByTagId.set(id, names)
          }
        }

        const resolved: PlantEquipmentRefs = {}
        if (groupsByTagId.size > 0) {
          const res = await read(EQUIPMENT_BY_TAG_CYPHER, {
            ids: [...groupsByTagId.keys()]
          })
          if (res.error) return { error: res.error }
          for (const row of res.rows as unknown as EquipmentByTagRow[]) {
            // The run resolver's precedence: equipment first, owning line
            // when the property hangs off the line itself.
            const ref: PMComponentRef | null = row.equipment_id
              ? {
                  scope: 'equipment',
                  id: row.equipment_id,
                  ...(row.equipment_name ? { name: row.equipment_name } : {})
                }
              : row.line_id
                ? {
                    scope: 'process_line',
                    id: row.line_id,
                    ...(row.line_name ? { name: row.line_name } : {})
                  }
                : null
            if (ref === null) continue
            for (const name of groupsByTagId.get(row.tag_id) ?? []) {
              resolved[name] ??= ref
            }
          }
        }

        const unresolvedNames = groups
          .map((group) => group.name)
          .filter((name) => resolved[name] === undefined)
        if (unresolvedNames.length > 0) {
          const res = await read(EQUIPMENT_BY_NAME_CYPHER, {
            names: unresolvedNames
          })
          if (res.error) return { error: res.error }
          for (const row of res.rows as unknown as EquipmentByNameRow[]) {
            if (!row.match?.id) continue
            resolved[row.query] = {
              scope: 'equipment',
              id: row.match.id,
              ...(row.match.name ? { name: row.match.name } : {})
            }
          }
        }

        return { data: resolved }
      },
      providesTags: ['Plant']
    }),

    /**
     * Resolve insight-chip refs (raw tag paths, process names, equipment
     * names) to graph ids so Agent Insights chips can deep-link. Already-
     * canonical URNs pass through; everything else anchors or name-matches.
     * Ambiguous names are omitted (plain badge), never a guessed link.
     */
    getPlantComponentRefs: builder.query<PlantComponentRefs, PMComponentRef[]>({
      queryFn: async (refs, _api, _extra, baseQuery) => {
        if (refs.length === 0) return { data: {} }

        const read = async (query: string, params: Record<string, unknown>) => {
          const res = await (baseQuery as CypherDrainBaseQuery)({
            url: '/cypher/read',
            method: 'POST',
            body: { query, params }
          })
          if (res.error) return { error: res.error }
          return {
            rows: (res.data as CypherReadResult | undefined)?.results ?? []
          }
        }

        const readChunks = async (query: string, paramKey: string, items: unknown[]) => {
          const pages = await Promise.all(
            chunkArray(items, CYPHER_UNWIND_CHUNK).map((slice) =>
              read(query, { [paramKey]: slice })
            )
          )
          const rows: unknown[] = []
          for (const page of pages) {
            if (page.error) return { error: page.error }
            rows.push(...page.rows)
          }
          return { rows }
        }

        const resolved: PlantComponentRefs = {}
        const keepDisplay = (raw: PMComponentRef, id: string, scope: PMComponentRef['scope']) => {
          resolved[plantComponentRefKey(raw)] = {
            scope,
            id,
            ...(raw.name ? { name: raw.name } : {})
          }
        }

        const tagRaws: PMComponentRef[] = []
        const anchorByPath = new Map<string, TagAnchorRef>()
        const processNames: PMComponentRef[] = []
        const equipmentNames: PMComponentRef[] = []

        for (const raw of refs) {
          if (raw.scope === 'tag') {
            const candidate = tagIdCandidate(raw.id)
            if (candidate.kind === 'id') {
              keepDisplay(raw, candidate.id, 'tag')
            } else {
              tagRaws.push(raw)
              anchorByPath.set(candidate.ref.path, candidate.ref)
            }
            continue
          }
          if (raw.scope === 'process_line') {
            if (isResolvedLineOrProcessId(raw.id)) keepDisplay(raw, raw.id, 'process_line')
            else processNames.push(raw)
            continue
          }
          if (isResolvedEquipmentId(raw.id)) keepDisplay(raw, raw.id, 'equipment')
          else equipmentNames.push(raw)
        }

        if (anchorByPath.size > 0) {
          const sorted = [...anchorByPath.values()].sort((a, b) => a.path.localeCompare(b.path))
          const res = await readChunks(TAG_ANCHOR_CYPHER, 'refs', sorted)
          if (res.error) return { error: res.error }
          const tagIdByPath = new Map<string, string>()
          for (const row of res.rows as TagAnchorRow[]) {
            if (row.tag_id) tagIdByPath.set(row.path, row.tag_id)
          }
          for (const raw of tagRaws) {
            const candidate = tagIdCandidate(raw.id)
            if (candidate.kind !== 'anchor') continue
            const tagId = tagIdByPath.get(candidate.ref.path)
            if (tagId) keepDisplay(raw, tagId, 'tag')
          }
        }

        if (processNames.length > 0) {
          const names = [...new Set(processNames.map((r) => r.id))].sort()
          const res = await readChunks(COMPONENT_BY_NAME_CYPHER, 'names', names)
          if (res.error) return { error: res.error }
          const byQuery = new Map<string, ComponentByNameRow>()
          for (const row of res.rows as ComponentByNameRow[]) byQuery.set(row.query, row)
          for (const raw of processNames) {
            const row = byQuery.get(raw.id)
            const picked = row ? pickComponentByName(row) : null
            const scope = picked ? componentScopeForLabel(picked.label) : null
            if (picked && scope) keepDisplay(raw, picked.id, scope)
          }
        }

        if (equipmentNames.length > 0) {
          const names = [...new Set(equipmentNames.map((r) => r.id))].sort()
          const res = await readChunks(EQUIPMENT_BY_NAME_CYPHER, 'names', names)
          if (res.error) return { error: res.error }
          for (const row of res.rows as EquipmentByNameRow[]) {
            if (row.matchCount !== 1 || !row.match?.id) continue
            for (const raw of equipmentNames) {
              if (raw.id !== row.query) continue
              keepDisplay(raw, row.match.id, 'equipment')
            }
          }
        }

        return { data: resolved }
      },
      providesTags: ['Plant']
    }),

    /**
     * Resolve the live-tag set for a given Line / Process / Equipment
     * node. Walks down to the equipment subtree, follows
     * `HAS_OBSERVABLE` / `HAS_PROPERTY` / `HAS_SETPOINT` to the
     * `MEASURED_BY` / `SET_BY` Tag/PLCTag endpoints, and resolves the
     * upstream `Compute` so `getPlantLiveTagValues` can immediately
     * start polling.
     */
    getPlantTagsByScope: builder.query<PlantTagsBundle, PlantTagScope>({
      query: ({ kind, id }) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: cypherForTagScope(kind), params: { nodeId: id } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, scope): PlantTagsBundle =>
        mapPlantTagsBundle(raw, scope),
      providesTags: (_r, _e, scope) => [
        { type: 'Plant', id: `Tags:${scope.kind}:${scope.id}` },
        'Plant'
      ]
    }),

    /**
     * Areas tab landing — every `:Area` in the ontology with summary
     * counts plus the site it is anchored to. Top-level areas are
     * grouped by site; areas not anchored to any site fall into the
     * stray bucket.
     */
    getPlantAreas: builder.query<PlantAreaIndex, void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(baseQuery as CypherDrainBaseQuery, PLANT_AREAS_CYPHER)
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const summaries: PlantAreaSummary[] = []
        for (const row of drained.data?.results ?? []) {
          const summary = mapPlantAreaSummaryRow(row)
          if (summary) summaries.push(summary)
        }
        return { data: groupAreaSummaries(summaries), meta: drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'Areas' }, 'Plant']
    }),

    /**
     * Detail bundle for a single Area: ancestor chain (root-first),
     * owning site, direct sub-areas with counts, and the Equipment /
     * Process / Part nodes placed in this area via `LOCATED_IN`.
     */
    getPlantAreaDetail: builder.query<PlantAreaDetail, string>({
      query: (areaId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_AREA_DETAIL_CYPHER, params: { areaId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, areaId): PlantAreaDetail =>
        mapPlantAreaDetail(raw, areaId),
      providesTags: (_r, _e, areaId) => [{ type: 'Plant', id: `Area:${areaId}` }, 'Plant']
    }),

    /**
     * Queries tab landing — every `:NamedQuery` node plus every
     * query-flavored `:Tag` (`tag_system='db'` or
     * `ignition_tag_type='query'`), each with the `:Database` it uses
     * and (for named queries) the count of tables it references. Drained
     * client-side so the tab gets every row, not the first page.
     */
    getPlantQueries: builder.query<PlantQuerySummary[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_QUERIES_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const summaries: PlantQuerySummary[] = []
        for (const row of drained.data?.results ?? []) {
          const summary = mapPlantQuerySummaryRow(row)
          if (summary) summaries.push(summary)
        }
        return { data: summaries, meta: drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'Queries' }, 'Plant']
    }),

    /**
     * Detail bundle for a single query (either a `:NamedQuery` or a
     * query-flavored `:Tag`): the node itself, the database it uses,
     * the owning compute host, and the tables it references.
     */
    getPlantQueryDetail: builder.query<PlantQueryDetail, string>({
      query: (queryId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_QUERY_DETAIL_CYPHER, params: { queryId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, queryId): PlantQueryDetail =>
        mapPlantQueryDetail(raw, queryId),
      providesTags: (_r, _e, queryId) => [{ type: 'Plant', id: `Query:${queryId}` }, 'Plant']
    }),

    /**
     * Safety tab landing — every `:ProtectiveFunction` with its owning
     * Line/Site, logic solver, and sensor/final-element counts. Drained
     * client-side to defeat the proxy's silent auto-`LIMIT 100`.
     */
    getPlantProtectiveFunctions: builder.query<PlantProtectiveFunctionSummary[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_PROTECTIVE_FUNCTIONS_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const summaries: PlantProtectiveFunctionSummary[] = []
        for (const row of drained.data?.results ?? []) {
          const summary = mapPlantProtectiveFunctionSummaryRow(row)
          if (summary) summaries.push(summary)
        }
        return { data: summaries, meta: drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'ProtectiveFunctions' }, 'Plant']
    }),

    /**
     * Detail bundle for a single `:ProtectiveFunction`: identity, owning
     * Line/Site, logic solver, SENSED_BY / ACTUATES devices, and PROTECTS
     * equipment.
     */
    getPlantProtectiveFunctionDetail: builder.query<PlantProtectiveFunctionDetail, string>({
      query: (functionId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_PROTECTIVE_FUNCTION_DETAIL_CYPHER, params: { functionId } }
      }),
      transformResponse: (
        raw: CypherReadResult,
        _meta,
        functionId
      ): PlantProtectiveFunctionDetail => mapPlantProtectiveFunctionDetail(raw, functionId),
      providesTags: (_r, _e, functionId) => [
        { type: 'Plant', id: `ProtectiveFunction:${functionId}` },
        'Plant'
      ]
    }),

    /**
     * Protective functions that touch one Equipment as a sensor, final
     * element, or protected asset. Drives the Safety chips on the
     * equipment detail header.
     */
    getPlantEquipmentProtectiveFunctions: builder.query<
      PlantEquipmentProtectiveFunctionLink[],
      string
    >({
      query: (equipmentId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_EQUIPMENT_PROTECTIVE_FUNCTIONS_CYPHER, params: { equipmentId } }
      }),
      transformResponse: (raw: CypherReadResult): PlantEquipmentProtectiveFunctionLink[] =>
        mapPlantEquipmentProtectiveFunctions(raw),
      providesTags: (_r, _e, equipmentId) => [
        { type: 'Plant', id: `Equipment:${equipmentId}` },
        { type: 'Plant', id: 'ProtectiveFunctions' },
        'Plant'
      ]
    }),

    /**
     * Safety tab FLD landing — every `:FunctionLogicDiagram` with its
     * owning LogicSolver/Site and SafetyPoint count. Drained client-side
     * to defeat the proxy's silent auto-`LIMIT 100`.
     */
    getPlantFunctionLogicDiagrams: builder.query<PlantFunctionLogicDiagramSummary[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_FUNCTION_LOGIC_DIAGRAMS_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const summaries: PlantFunctionLogicDiagramSummary[] = []
        for (const row of drained.data?.results ?? []) {
          const summary = mapPlantFunctionLogicDiagramSummaryRow(row)
          if (summary) summaries.push(summary)
        }
        return { data: summaries, meta: drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'FunctionLogicDiagrams' }, 'Plant']
    }),

    /**
     * Detail bundle for a single `:LogicSolver`: identity, site, contained
     * FLDs, and ProtectiveFunctions that `SOLVED_BY` this solver.
     */
    getPlantLogicSolverDetail: builder.query<PlantLogicSolverDetail, string>({
      query: (solverId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_LOGIC_SOLVER_DETAIL_CYPHER, params: { solverId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, solverId): PlantLogicSolverDetail =>
        mapPlantLogicSolverDetail(raw, solverId),
      providesTags: (_r, _e, solverId) => [
        { type: 'Plant', id: `LogicSolver:${solverId}` },
        'Plant'
      ]
    }),

    /**
     * Detail bundle for a single `:FunctionLogicDiagram`: points plus
     * ProtectiveFunctions that `IMPLEMENTED_BY` this FLD.
     */
    getPlantFunctionLogicDiagramDetail: builder.query<PlantFunctionLogicDiagramDetail, string>({
      query: (fldId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_FUNCTION_LOGIC_DIAGRAM_DETAIL_CYPHER, params: { fldId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, fldId): PlantFunctionLogicDiagramDetail =>
        mapPlantFunctionLogicDiagramDetail(raw, fldId),
      providesTags: (_r, _e, fldId) => [
        { type: 'Plant', id: `FunctionLogicDiagram:${fldId}` },
        'Plant'
      ]
    }),

    /**
     * Detail bundle for a single `:SafetyPoint`: parent FLD/solver,
     * implementing ProtectiveFunctions, and mapped Equipment.
     */
    getPlantSafetyPointDetail: builder.query<PlantSafetyPointDetail, string>({
      query: (pointId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_SAFETY_POINT_DETAIL_CYPHER, params: { pointId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, pointId): PlantSafetyPointDetail =>
        mapPlantSafetyPointDetail(raw, pointId),
      providesTags: (_r, _e, pointId) => [{ type: 'Plant', id: `SafetyPoint:${pointId}` }, 'Plant']
    }),

    /**
     * Safety Manager points that map onto one Equipment via
     * `MAPS_TO_SAFETY_POINT`. Drives the Safety Manager section on
     * equipment detail.
     */
    getPlantEquipmentSafetyPoints: builder.query<PlantEquipmentSafetyPointLink[], string>({
      query: (equipmentId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_EQUIPMENT_SAFETY_POINTS_CYPHER, params: { equipmentId } }
      }),
      transformResponse: (raw: CypherReadResult): PlantEquipmentSafetyPointLink[] =>
        mapPlantEquipmentSafetyPoints(raw),
      providesTags: (_r, _e, equipmentId) => [
        { type: 'Plant', id: `Equipment:${equipmentId}` },
        { type: 'Plant', id: 'FunctionLogicDiagrams' },
        'Plant'
      ]
    }),

    /**
     * Live tag-value snapshot for a set of Ignition paths against one
     * gateway. Wraps the proxy's `/live-data/tags` pass-through and
     * normalizes the upstream payload (array of `{tagPath, value, …}`
     * rows OR `path -> value` map) into a flat array so the renderer
     * doesn't have to branch. Consumers decide whether to take a one-shot
     * snapshot or opt into polling.
     */
    getPlantLiveTagValues: builder.query<PlantTagValuesResponse, PlantLiveTagValuesArgs>({
      query: ({ paths, gateway_url, project }) => ({
        url: '/live-data/tags',
        method: 'POST',
        body: {
          paths,
          gateway_url,
          ...(project ? { project } : {})
        }
      }),
      transformResponse: (raw: { tags?: unknown }): PlantTagValuesResponse => ({
        tags: normalizeLiveTagValues(raw?.tags)
      })
    }),

    /**
     * Live tag-value snapshot keyed by graph node ID. The server serves cached
     * readings first and returns unregistered IDs as per-item errors. Large
     * Plant scopes are split into bounded requests before they reach the
     * proxy's per-request ID limit.
     */
    getPlantLiveTagValuesById: builder.query<PlantTagIdValuesResponse, PlantTagIdValuesArgs>({
      queryFn: async ({ node_ids }, _api, _extra, baseQuery) => ({
        data: await readPlantLiveTagValuesById(baseQuery as CypherDrainBaseQuery, node_ids)
      })
    }),

    /**
     * Schedules-tab landing snapshot. Returns every `:Compute` that owns
     * at least one `:ScheduleProfile` or `:Holiday` (so the user can pick
     * a gateway), with the per-gateway counts to render the tiles. The
     * live "is-active" pull is intentionally a separate query
     * ({@link useGetActiveSchedulesQuery}) so this snapshot can be cached
     * with the usual `Plant` tag while the active pull polls.
     */
    getPlantScheduleGateways: builder.query<PlantScheduleGateway[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_SCHEDULE_GATEWAYS_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const out: PlantScheduleGateway[] = []
        for (const row of drained.data?.results ?? []) {
          const gateway = mapPlantScheduleGatewayRow(row)
          if (gateway) out.push(gateway)
        }
        return { data: out, meta: drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'Schedules' }, 'Plant']
    }),

    /**
     * Live active-status snapshot for every `:ScheduleProfile` and
     * `:Holiday` scoped to a given gateway. Backed by
     * `POST /api/ontology/schedules/active`, which reads the registered
     * `[System]Gateway/CurrentDateTime` tag by node ID and runs the pure
     * `evaluateActiveSchedules` library — see
     * `apps/axilon-api-service/src/routes/ontology/schedules-routes.ts`
     * for the server side. Poll this from the UI to keep the status dot
     * fresh.
     */
    getActiveSchedules: builder.query<ActiveSchedulesResponse, ActiveSchedulesArgs>({
      query: ({ gateway_url, project_name }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/schedules/active`,
        method: 'POST',
        body: {
          gateway_url,
          ...(project_name ? { project_name } : {})
        }
      })
    }),

    /**
     * Alarms-tab landing. Returns every `:Alarm` in the graph paired
     * with its source `:Tag` and (when present) the parent
     * `:UDTDefinition` / `:UDTInstance` + upstream `:Compute`. The
     * flat list is then pre-bucketed into UDTDefinition > UDTInstance
     * groups so the grouped view doesn't have to re-group on every
     * render; standalone tag alarms land in `ungrouped`.
     *
     * Drained client-side via the shared pagination loop — the
     * unfiltered cross-product easily outgrows a single page on plants
     * with dozens of UDT instances each carrying several alarms.
     * Priority filtering happens server-side via the optional
     * `$priorities` allow-list.
     */
    getPlantAlarms: builder.query<PlantAlarmsIndex, PlantAlarmsArgs | void>({
      queryFn: async (arg, _api, _extra, baseQuery) => {
        const priorities = (arg ?? {}).priorities
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_ALARMS_CYPHER,
          // Cypher needs an explicit null for the IS NULL check above when
          // the filter is omitted; an undefined param would 400.
          {
            priorities: priorities && priorities.length > 0 ? priorities : null
          }
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const alarms: PlantAlarm[] = []
        for (const row of drained.data?.results ?? []) {
          const alarm = mapPlantAlarmRow(row)
          if (alarm) alarms.push(alarm)
        }
        return { data: groupAlarmsByDefinition(alarms), meta: drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'Alarms' }, 'Plant']
    }),

    /**
     * Plant Viewer Alarms — live status fan-out.
     *
     * The proxy's `/live-data/alarms/active` route exposes Ignition's
     * `system.alarm.queryStatus` for a single gateway. A typical plant
     * spans multiple Ignition gateways, so this endpoint fans out one
     * `GET` per unique `gateway_url`, merges the results, and stamps
     * `gateway_url` onto each row so the page can match an active
     * alarm back to its configured `PlantAlarm` without ambiguity
     * (alarm sources are not globally unique across gateways).
     *
     * Per-gateway errors are collected into the response rather than
     * failing the whole call, mirroring the historian-batch handler's
     * "best-effort" contract — one offline gateway shouldn't blank
     * the entire live-status panel.
     *
     * Not tagged with `Plant` (it doesn't read from Neo4j) so refetch
     * is driven exclusively by the page's manual "Refresh" button and
     * mount-time fetch.
     */
    getPlantActiveAlarms: builder.query<PlantActiveAlarmsResponse, PlantActiveAlarmsArgs>({
      queryFn: async ({ gateways }, _api, _extra, baseQuery) => {
        if (!gateways || gateways.length === 0) {
          return { data: { alarms: [], errors: {} } }
        }

        // Deduplicate by (gateway_url, project) so the same gateway isn't
        // queried twice when many alarms share it.
        const seen = new Set<string>()
        const unique: PlantActiveAlarmsGateway[] = []
        for (const g of gateways) {
          if (!g.gateway_url) continue
          const key = `${g.gateway_url}::${g.project ?? ''}`
          if (seen.has(key)) continue
          seen.add(key)
          unique.push(g)
        }
        if (unique.length === 0) {
          return { data: { alarms: [], errors: {} } }
        }

        const responses = await Promise.all(
          unique.map(async (g) => {
            const params: Record<string, string> = {
              gateway_url: g.gateway_url
            }
            if (g.project) params.project = g.project
            const result = await baseQuery({
              url: '/live-data/alarms/active',
              method: 'GET',
              params
            })
            return { gateway: g, result }
          })
        )

        const alarms: PlantActiveAlarm[] = []
        const errors: Record<string, string> = {}
        let lastMeta: FetchBaseQueryMeta | undefined

        for (const { gateway, result } of responses) {
          if (result.meta) lastMeta = result.meta
          if (result.error) {
            errors[gateway.gateway_url] = extractFetchErrorMessage(result.error)
            continue
          }
          const body = result.data as { alarms?: unknown } | undefined
          const rawAlarms = body?.alarms
          if (!Array.isArray(rawAlarms)) continue
          for (const item of rawAlarms) {
            const normalized = normalizeActiveAlarm(item, gateway.gateway_url)
            if (normalized) alarms.push(normalized)
          }
        }

        return { data: { alarms, errors }, meta: lastMeta }
      }
    }),

    /**
     * All compute hosts with the per-host counts the Compute-tab
     * landing grid needs in one round-trip, plus standalone
     * `:Database` nodes (tabular dumps) projected as `db_server` hosts.
     * Ordered case-insensitively by name so the grid is stable across
     * page renders.
     *
     * Drained client-side via the shared pagination loop — see
     * `drainCypherRows` for the full rationale.
     */
    getPlantComputeIndex: builder.query<PlantComputeHostSummary[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drain = baseQuery as CypherDrainBaseQuery
        const [drained, standalone] = await Promise.all([
          drainCypherRows(drain, PLANT_COMPUTE_INDEX_CYPHER),
          drainCypherRows(drain, PLANT_STANDALONE_DATABASE_INDEX_CYPHER)
        ])
        if (drained.error) return { error: drained.error, meta: drained.meta }
        if (standalone.error) return { error: standalone.error, meta: standalone.meta }
        const out: PlantComputeHostSummary[] = []
        for (const row of [...(drained.data?.results ?? []), ...(standalone.data?.results ?? [])]) {
          const summary = mapPlantComputeIndexRow(row)
          if (summary) out.push(summary)
        }
        out.sort(compareByNameThenId)
        return { data: out, meta: standalone.meta ?? drained.meta }
      },
      providesTags: () => [{ type: 'Plant', id: 'Compute' }, 'Plant']
    }),

    /**
     * Detail bundle for a single Compute host. Returns the host node
     * plus every TagProvider attached via `HAS_TAG_PROVIDER` and every
     * Database attached via `HAS_DATABASE` (each with its
     * `CONTAINS_TABLE`-reachable Table count), the mapped
     * `:Controller` nodes, the inbound `:NETWORKS_WITH` gateways, and
     * every `Equipment` / `Line` monitored by the host. `$computeId`
     * may also be a standalone dump `:Database` URN.
     */
    getPlantComputeDetail: builder.query<PlantComputeHostDetail, string>({
      query: (computeId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_COMPUTE_DETAIL_CYPHER, params: { computeId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, computeId): PlantComputeHostDetail =>
        mapPlantComputeDetail(raw, computeId),
      providesTags: (_r, _e, computeId) => [{ type: 'Plant', id: `Compute:${computeId}` }, 'Plant']
    }),

    /**
     * Flat list of every PLC node that participates in the navigation
     * tree — Controllers, Programs, Routines, and AOIs — with their
     * parent ids so the left rail in {@link PlcTreeNav} can rebuild
     * the hierarchy client-side (same pattern as
     * {@link useGetPlantProcessTreeQuery}).
     */
    getPlantPlcTree: builder.query<PlantPlcTree, void>({
      query: () => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_PLC_TREE_CYPHER }
      }),
      transformResponse: (raw: CypherReadResult): PlantPlcTree => mapPlantPlcTree(raw),
      providesTags: () => [{ type: 'Plant', id: 'PlcTree' }, 'Plant']
    }),

    /**
     * Detail bundle for a single PLC node — works for any of
     * Controller / Program / Routine / AOI / PLCTag. Dispatches on
     * `labels(n)` server-side so the client can issue one round-trip
     * regardless of the selected node's kind, and switches over the
     * discriminated {@link PlantPlcNodeDetail} on the way back.
     */
    getPlantPlcControllerTags: builder.query<
      PlantPlcControllerTagsPage,
      PlantPlcControllerTagsArgs
    >({
      query: ({ nodeId, page, pageSize = 50 }) => {
        const resolvedPage = Math.max(0, Math.floor(page))
        const resolvedPageSize = Math.max(1, Math.floor(pageSize))
        return {
          url: '/cypher/read',
          method: 'POST',
          body: {
            query: PLANT_PLC_CONTROLLER_TAGS_CYPHER,
            params: {
              nodeId,
              skip: resolvedPage * resolvedPageSize,
              pageSize: resolvedPageSize
            }
          }
        }
      },
      transformResponse: (raw: CypherReadResult): PlantPlcControllerTagsPage =>
        mapPlantPlcControllerTagsPage(raw),
      providesTags: (_r, _e, { nodeId }) => [
        { type: 'Plant', id: `PlcControllerTags:${nodeId}` },
        'Plant'
      ]
    }),

    getPlantPlcNodeDetail: builder.query<PlantPlcNodeDetail, string>({
      query: (nodeId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_PLC_NODE_DETAIL_CYPHER, params: { nodeId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, nodeId): PlantPlcNodeDetail =>
        mapPlantPlcNodeDetail(raw, nodeId),
      providesTags: (_r, _e, nodeId) => [{ type: 'Plant', id: `Plc:${nodeId}` }, 'Plant']
    }),

    /**
     * Flat DCS navigation tree — DcsController, Cee, ControlModule,
     * FunctionBlock, IoModule, IoChannel — with parent ids so the left
     * rail in {@link DcsTreeNav} can rebuild the hierarchy client-side
     * (same pattern as {@link useGetPlantPlcTreeQuery}).
     */
    getPlantDcsTree: builder.query<PlantDcsTree, void>({
      query: () => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_DCS_TREE_CYPHER }
      }),
      transformResponse: (raw: CypherReadResult): PlantDcsTree => mapPlantDcsTree(raw),
      providesTags: () => [{ type: 'Plant', id: 'DcsTree' }, 'Plant']
    }),

    /**
     * Detail bundle for a single DCS node — DcsController / Cee /
     * ControlModule / FunctionBlock / IoModule / IoChannel / ControlTag
     * / ControlAlarm / ControlSetpoint. Dispatches on `labels(n)` so
     * the client issues one round-trip regardless of kind.
     */
    getPlantDcsNodeDetail: builder.query<PlantDcsNodeDetail, string>({
      query: (nodeId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_DCS_NODE_DETAIL_CYPHER, params: { nodeId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, nodeId): PlantDcsNodeDetail =>
        mapPlantDcsNodeDetail(raw, nodeId),
      providesTags: (_r, _e, nodeId) => [{ type: 'Plant', id: `Dcs:${nodeId}` }, 'Plant']
    }),

    /**
     * Flat Network navigation tree — Asset, NetworkSegment, NetworkDevice —
     * with parent ids so the left rail in {@link NetworkTreeNav} can rebuild
     * the enterprise/PCN hierarchy client-side (Experion EMDB ingest). Same
     * pattern as {@link useGetPlantDcsTreeQuery}.
     */
    getPlantNetworkTree: builder.query<PlantNetworkTree, void>({
      query: () => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_NETWORK_TREE_CYPHER }
      }),
      transformResponse: (raw: CypherReadResult): PlantNetworkTree => mapPlantNetworkTree(raw),
      providesTags: () => [{ type: 'Plant', id: 'NetworkTree' }, 'Plant']
    }),

    /**
     * Detail bundle for a single Network node — Asset / NetworkSegment /
     * NetworkDevice. Dispatches on `labels(n)` so the client issues one
     * round-trip regardless of kind; Compute nodes surface as cross-links.
     */
    getPlantNetworkNodeDetail: builder.query<PlantNetworkNodeDetail, string>({
      query: (nodeId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_NETWORK_NODE_DETAIL_CYPHER, params: { nodeId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, nodeId): PlantNetworkNodeDetail =>
        mapPlantNetworkNodeDetail(raw, nodeId),
      providesTags: (_r, _e, nodeId) => [{ type: 'Plant', id: `Network:${nodeId}` }, 'Plant']
    }),

    /**
     * Flat RobotController -> RobotArm -> RobotJob tree. Drained
     * client-side so large controller backups do not lose jobs after
     * the proxy's default `/cypher/read` cap.
     */
    getPlantRoboticsTree: builder.query<PlantRoboticsTree, void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          PLANT_ROBOTICS_TREE_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        return {
          data: mapPlantRoboticsTree(drained.data ?? { count: 0, results: [] }),
          meta: drained.meta
        }
      },
      providesTags: () => [{ type: 'Plant', id: 'RoboticsTree' }, 'Plant']
    }),

    /**
     * Detail bundle for one Robotics node: controller / arm / job plus
     * side nodes for I/O signals and conditions.
     */
    getPlantRobotNodeDetail: builder.query<PlantRobotNodeDetail, string>({
      query: (nodeId) => ({
        url: '/cypher/read',
        method: 'POST',
        body: { query: PLANT_ROBOT_NODE_DETAIL_CYPHER, params: { nodeId } }
      }),
      transformResponse: (raw: CypherReadResult, _meta, nodeId): PlantRobotNodeDetail =>
        mapPlantRobotNodeDetail(raw, nodeId),
      providesTags: (_r, _e, nodeId) => [{ type: 'Plant', id: `Robotics:${nodeId}` }, 'Plant']
    }),

    /**
     * Describe tables/columns for a registered connection
     * (`GET /db/connections/{id}/schema`). `connectionId` is the graph
     * `:Database` URN. Powers the table-picker rail in the DB query
     * engine. Cached per connection id; no graph invalidation (schema
     * is a side-channel the ontology doesn't model).
     */
    getPlantDbSchema: builder.query<PlantDbSchemaResponse, PlantDbSchemaArgs>({
      query: (args) => ({
        url: buildPlantDbSchemaUrl(args),
        method: 'GET'
      }),
      transformResponse: (raw: unknown): PlantDbSchemaResponse => mapPlantDbSchema(raw)
    }),

    /**
     * Read a database query via the connection-addressed plane
     * (`POST …/db/connections/{id}/query`, `query_type: read`). Modeled
     * as a mutation because Run is an explicit user action. Transport
     * is resolved server-side from the registry.
     */
    executePlantDbReadQuery: builder.mutation<PlantDbExecuteResult, PlantDbExecuteArgs>({
      query: (args) => ({
        url: buildPlantDbQueryUrl(args.connectionId),
        method: 'POST',
        body: buildPlantDbQueryBody(args, 'read')
      }),
      transformResponse: (raw: unknown): PlantDbExecuteResult => mapPlantDbExecuteResult(raw)
    }),
    /**
     * Run a mutating (INSERT/UPDATE/DELETE/DDL) query on the same
     * connection-addressed plane with `query_type: write`. This is the
     * only client tooling wired to write — the human Plant DB query
     * engine's "Write mode". Agents and the dashboard sandbox stay
     * read-only.
     */
    executePlantDbWriteQuery: builder.mutation<PlantDbExecuteResult, PlantDbExecuteArgs>({
      query: (args) => ({
        url: buildPlantDbQueryUrl(args.connectionId),
        method: 'POST',
        body: buildPlantDbQueryBody(args, 'write')
      }),
      transformResponse: (raw: unknown): PlantDbExecuteResult => mapPlantDbExecuteResult(raw)
    }),

    /**
     * Offset-paginated, server-searched tags hosted by one TagProvider
     * via `:PROVIDES`, PLUS the leaf `:Tag` entries reachable through
     * this provider's `:UDTInstance` HAS_MEMBER tree. Powers the flat
     * view of the tag-provider browser panel on
     * `/plant/compute/hosts/:computeId`.
     *
     * See `PLANT_PROVIDER_TAGS_CYPHER` for the union-of-branches /
     * filter-and-page math; this endpoint is a thin RTK Query wrapper
     * that forwards args, applies `mapPlantProviderTagsPage`, and
     * scopes cache invalidation per-provider.
     */
    getPlantProviderTags: builder.query<PlantProviderTagsPage, PlantProviderTagsArgs>({
      query: ({ providerId, search, page, pageSize }) => {
        const resolvedPageSize = pageSize ?? 100
        const resolvedPage = Math.max(0, Math.floor(page ?? 0))
        return {
          url: '/cypher/read',
          method: 'POST',
          body: {
            query: PLANT_PROVIDER_TAGS_CYPHER,
            params: {
              providerId,
              search: search?.trim() || null,
              skip: resolvedPage * resolvedPageSize,
              pageSize: resolvedPageSize
            }
          }
        }
      },
      transformResponse: (raw: CypherReadResult): PlantProviderTagsPage =>
        mapPlantProviderTagsPage(raw),
      providesTags: (_r, _e, { providerId }) => [
        { type: 'Plant', id: `Provider:${providerId}` },
        'Plant'
      ]
    }),

    /**
     * One level of the tag-provider folder tree, fetched dynamically
     * per navigation step. Returns the tags whose `folder` equals
     * `$path`, the `:UDTInstance` nodes anchored at exactly that
     * `$path` (rendered as folder rows; clicking transitions to
     * UDT-instance navigation via {@link useGetPlantUdtFolderNodeQuery}),
     * and first-segment subfolders with recursive leaf counts. Caches
     * by `(providerId, path)` so breadcrumb / back navigation is a
     * cache hit. See `PLANT_PROVIDER_FOLDER_NODE_CYPHER`.
     */
    getPlantProviderFolderNode: builder.query<
      PlantProviderFolderNodeResponse,
      PlantProviderFolderNodeArgs
    >({
      query: ({ providerId, path }) => ({
        url: '/cypher/read',
        method: 'POST',
        body: {
          query: PLANT_PROVIDER_FOLDER_NODE_CYPHER,
          params: { providerId, currentPath: path }
        }
      }),
      transformResponse: (raw: CypherReadResult): PlantProviderFolderNodeResponse =>
        mapPlantProviderFolderNodeResponse(raw),
      providesTags: (_r, _e, { providerId }) => [
        { type: 'Plant', id: `Provider:${providerId}` },
        'Plant'
      ]
    }),

    /**
     * One level of a `:UDTInstance`'s member tree, fetched per
     * navigation step inside the tag-provider browser's explorer view
     * (the analog of `getPlantProviderFolderNode`, but rooted in a UDT
     * context rather than a tag-folder path). See
     * `PLANT_UDT_FOLDER_NODE_CYPHER` for the partitioning of direct
     * leaves vs nested instances vs subfolders.
     */
    getPlantUdtFolderNode: builder.query<PlantUdtFolderNodeResponse, PlantUdtFolderNodeArgs>({
      query: ({ udtInstanceId, definitionId, subPath }) => ({
        url: '/cypher/read',
        method: 'POST',
        body: {
          query: PLANT_UDT_FOLDER_NODE_CYPHER,
          params: { udtInstanceId, definitionId, subPath }
        }
      }),
      transformResponse: (raw: CypherReadResult, _meta, args): PlantUdtFolderNodeResponse =>
        mapPlantUdtFolderNodeResponse(raw, args),
      providesTags: (_r, _e, { udtInstanceId }) => [
        { type: 'Plant', id: `UDTInstance:${udtInstanceId}` },
        'Plant'
      ]
    })
  })
})

export const {
  useGetPlantEntityQuery,
  useGetPlantEntityConnectionsQuery,
  useGetPlantLabelCountsQuery,
  useGetPlantStrayCountsQuery,
  useGetPlantLabelEntitiesQuery,
  useGetPlantSitesQuery,
  useGetPlantSiteOptionsQuery,
  useGetPlantSiteDetailQuery,
  useGetPlantLineInventoryQuery,
  useGetPlantLineDetailQuery,
  useGetPlantLineControlCoverageQuery,
  useGetPlantProcessDetailQuery,
  useGetPlantProcessTreeQuery,
  useGetPlantEquipmentDetailQuery,
  useGetPlantEquipmentRelationshipsQuery,
  useGetPlantPartDetailQuery,
  useGetPlantVendorDetailQuery,
  useGetPlantProcedureDetailQuery,
  useGetPlantUtilityDetailQuery,
  useGetPlantLineOperationsQuery,
  useGetPlantEquipmentRefsQuery,
  useGetPlantComponentRefsQuery,
  useGetPlantTagsByScopeQuery,
  useGetPlantAreasQuery,
  useGetPlantAreaDetailQuery,
  useGetPlantQueriesQuery,
  useGetPlantQueryDetailQuery,
  useGetPlantProtectiveFunctionsQuery,
  useGetPlantProtectiveFunctionDetailQuery,
  useGetPlantEquipmentProtectiveFunctionsQuery,
  useGetPlantFunctionLogicDiagramsQuery,
  useGetPlantLogicSolverDetailQuery,
  useGetPlantFunctionLogicDiagramDetailQuery,
  useGetPlantSafetyPointDetailQuery,
  useGetPlantEquipmentSafetyPointsQuery,
  useGetPlantLiveTagValuesQuery,
  useLazyGetPlantLiveTagValuesQuery,
  useGetPlantLiveTagValuesByIdQuery,
  useLazyGetPlantLiveTagValuesByIdQuery,
  useGetPlantScheduleGatewaysQuery,
  useGetActiveSchedulesQuery,
  useGetPlantAlarmsQuery,
  useGetPlantActiveAlarmsQuery,
  useGetPlantComputeIndexQuery,
  useGetPlantComputeDetailQuery,
  useGetPlantPlcTreeQuery,
  useGetPlantPlcControllerTagsQuery,
  useGetPlantPlcNodeDetailQuery,
  useGetPlantDcsTreeQuery,
  useGetPlantDcsNodeDetailQuery,
  useGetPlantNetworkTreeQuery,
  useGetPlantNetworkNodeDetailQuery,
  useGetPlantRoboticsTreeQuery,
  useGetPlantRobotNodeDetailQuery,
  useGetPlantDbSchemaQuery,
  useExecutePlantDbReadQueryMutation,
  useExecutePlantDbWriteQueryMutation,
  useGetPlantProviderTagsQuery,
  useGetPlantProviderFolderNodeQuery,
  useGetPlantUdtFolderNodeQuery
} = plantViewerApi
